import { TractRequiredField, TractRequirementType, ScalingRegion } from '@/utils/Enumerations.js'
import i18n from '@/i18n'
import fieldRules from '@/utils/rules.js'

const hasValue = (value) => value !== null && value !== undefined
const hasAny = (list) => (list?.length ?? 0) > 0

// Tract objects arrive either as the server response (nested objects) or as the tract form's
// working copy (flattened ids), so each check accepts both. Callers pass a grouped tract's group landowners.
const satisfiedBy = {
  [TractRequiredField.Forester.value]: (t) => hasValue(t.foresterUserId ?? t.foresterUser?.applicationUserId),
  [TractRequiredField.LoggerAccount.value]: (t) => hasValue(t.loggerAccountId),
  [TractRequiredField.SupplierAccount.value]: (t) => hasValue(t.supplierAccountId),
  [TractRequiredField.HaulerAccount.value]: (t) => hasValue(t.haulerAccountId),
  [TractRequiredField.ConsultingForesterAccount.value]: (t) => hasValue(t.consultingForesterAccountId),
  [TractRequiredField.Team.value]: (t) => hasValue(t.teamId ?? t.team?.teamId),
  [TractRequiredField.HarvestType.value]: (t) => hasValue(t.harvestTypeId ?? t.harvestType?.harvestTypeId),
  [TractRequiredField.LoggingMethod.value]: (t) => hasValue(t.loggingMethodId ?? t.loggingMethod?.loggingMethodId),
  [TractRequiredField.TimberCategory.value]: (t) => hasValue(t.timberCategoryId ?? t.timberCategory?.timberCategoryId),
  [TractRequiredField.Conversion.value]: (t) => hasValue(t.conversionId ?? t.conversion?.conversionId),
  [TractRequiredField.LoggingAccessibilityTag.value]: (t) => hasValue(t.loggingAccessibilityTagId),
  [TractRequiredField.MapAndParcelNumber.value]: (t) => (t.mapAndParcelNumber ?? '').trim() !== '',
  [TractRequiredField.Acres.value]: (t) => Number(t.acres) > 0,
  [TractRequiredField.Cost.value]: (t) => Number(t.cost) > 0,
  [TractRequiredField.HarvestStartDate.value]: (t) => hasValue(t.harvestStartDate),
  [TractRequiredField.HarvestEndDate.value]: (t) => hasValue(t.harvestEndDate),
  [TractRequiredField.AvailabilityStartDate.value]: (t) => hasValue(t.availabilityStartDate),
  [TractRequiredField.AvailabilityEndDate.value]: (t) => hasValue(t.availabilityEndDate),
  [TractRequiredField.PurchaseDate.value]: (t) => hasValue(t.purchaseDate),
  [TractRequiredField.ScalingRegion.value]: (t) => hasValue(t.scalingRegion) && t.scalingRegion !== ScalingRegion.NotDefined.value,
  [TractRequiredField.Loggers.value]: (t) => hasAny(t.loggerIds ?? t.loggers),
  [TractRequiredField.Landowners.value]: (t, { landowners }) => landowners ? hasAny(landowners) : t.landownerCount > 0,
  [TractRequiredField.PermitNumbers.value]: (t) => hasAny(t.permitNumbers),
  [TractRequiredField.Certifications.value]: (t) => hasAny(t.certificationIds ?? t.tractCertifications),
  // The tract response lists only product contents; species-based cruise data arrives as a count.
  [TractRequiredField.TractContents.value]: (t) => t.speciesContentCount > 0 || hasAny(t.tractContents)
}

// Tracts in a group take these from the group, so only the group can satisfy or clear them.
export const groupInheritedFields = new Set([
  TractRequiredField.Forester,
  TractRequiredField.ConsultingForesterAccount,
  TractRequiredField.Team,
  TractRequiredField.Landowners
].map(f => f.value))

// Without a requirement type every rule is returned, which is what adding a contract checks.
export const requiredFieldsForTractType = (tractType, requirementType) => {
  return (tractType?.requiredFields ?? [])
    .filter(rule => requirementType === undefined || rule.requirementType === requirementType.value)
    .map(rule => TractRequiredField.forInt(rule.field))
    .filter(Boolean)
}

export const missingRequiredFields = (tract, requiredFields, context = {}) => {
  if (!tract) return requiredFields
  return requiredFields.filter(field => !satisfiedBy[field.value](tract, context))
}

export const missingGroupRequiredFields = (tractGroup, tractType) => {
  const createFields = requiredFieldsForTractType(tractType, TractRequirementType.Create).filter(field => groupInheritedFields.has(field.value))
  return missingRequiredFields(tractGroup, createFields, { landowners: tractGroup?.landowners ?? [] })
}

export const clearedRequiredFields = (before, after, requiredFields, contextBefore = {}, contextAfter = {}) => {
  if (!before || !after) return []
  return requiredFields.filter(field => satisfiedBy[field.value](before, contextBefore) && !satisfiedBy[field.value](after, contextAfter))
}

// Pickers and tooltips show the field's name; toasts and alerts about a missing field may need a fuller sentence.
const messageText = {
  [TractRequiredField.Loggers.value]: 'atLeastOneLoggerRequired'
}

export const requiredFieldNames = (fields) => fields.map(f => f.name).join(', ')

export const requiredFieldMessages = (fields) => fields.map(f => messageText[f.value] ? i18n.t(messageText[f.value]) : f.name).join(', ')

const listFields = new Set([
  TractRequiredField.Loggers,
  TractRequiredField.Landowners,
  TractRequiredField.PermitNumbers,
  TractRequiredField.Certifications,
  TractRequiredField.TractContents
].map(f => f.value))

const noAnnotation = Object.freeze({ hint: '', required: false, rules: [] })

// Several fields may share one input (a date range summary); a Create rule on any of them wins.
export const requirementAnnotation = (tractType, fields) => {
  const wanted = [fields].flat().map(f => f.value)
  const matching = (tractType?.requiredFields ?? []).filter(rule => wanted.includes(rule.field))
  if (matching.length === 0) return noAnnotation
  const atCreation = matching.some(rule => rule.requirementType === TractRequirementType.Create.value)
  const isList = wanted.some(value => listFields.has(value))
  return {
    hint: atCreation ? i18n.t('required') : i18n.t('requiredBeforeAddingContract'),
    required: atCreation,
    rules: atCreation ? [isList ? fieldRules.rules.requiredSelect : fieldRules.rules.required] : []
  }
}
